/**
 * Cash Calculations Utility
 * Centralized logic for calculating expected cash in the register
 * Used by both Dashboard.js and LiveReport.js
 */

/**
 * Calculate expected cash based on transactions and initial cash
 * @param {Object} cashRegister - The cash register object with transactions
 * @param {Object|null} pendingVentas - Optional pending sales/prizes from localStorage
 * @returns {Object} { expectedColones, expectedDollars }
 */
export function calculateExpectedCash(cashRegister, pendingVentas = null) {
  if (!cashRegister) return { expectedColones: 0, expectedDollars: 0 };
  
  let expectedColones = cashRegister.initial_cash_colones || 0;
  let expectedDollars = cashRegister.initial_cash_dollars || 0;

  cashRegister.transactions?.forEach(t => {
    // Skip voided transactions EXCEPT for venta_inventario
    // (venta_inventario voided should still count because nota_credito_inventario cancels it)
    if (t.voided && t.type !== "venta_inventario") return;
    
    // For voided venta_inventario, we still count it because the nota_credito will cancel it
    // This way: venta (+1000) + nota_credito (-1000) = 0
    
    // Pago en Efectivo: SÍ afecta caja (suma efectivo)
    if (t.type === "pago_efectivo") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
      return;
    }
    
    // Si es pago_con_premio, no afecta efectivo (solo es registro)
    if (t.type === "pago_con_premio") {
      return;
    }
    
    if (t.type === "venta") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "venta_inventario") {
      // Venta de inventario en efectivo: SUMA al efectivo (incluso si está voided)
      // La nota de crédito se encarga de restar
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "pago_electronico_inventario" || t.type === "sinpe_inventario" || t.type === "tarjeta_inventario") {
      // Venta de inventario por SINPE/Tarjeta: NO afecta efectivo físico (va al banco)
      // No suma ni resta al efectivo en caja
      return;
    } else if (t.type === "nota_credito_inventario") {
      // Nota de crédito de inventario: RESTA del efectivo (devolución)
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "premio") {
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "entrada") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "salida") {
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "salida_premios") {
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "pago_dias_anteriores_afecta") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "pagos_del_dia") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "cuenta_por_cobrar") {
      // Cuenta por cobrar: RESTA porque es una venta donde el dinero NO entró a caja
      // Cuando el cliente pague, se sumará de vuelta (si paga en efectivo)
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "aumento_cuenta_por_cobrar") {
      // Aumento de cuenta por cobrar: RESTA (más crédito dado al cliente)
      expectedColones -= t.amount_colones;
      expectedDollars -= t.amount_dollars;
    } else if (t.type === "cuenta_por_pagar") {
      // Cuenta por pagar: SUMA porque es dinero/mercadería que se recibió a crédito
      // Ejemplo: si falta dinero (-79000) y se crea CxP de 79000, queda en 0
      // Al día siguiente cuando se PAGUE, se crea una SALIDA que resta
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type.startsWith("banco_")) {
      // Movimientos de banco
      if (t.type.endsWith("_salida")) {
        // Salida de banco: dinero sale del banco para cubrir gastos
        // SUMA al efectivo esperado porque el banco cubre el gasto
        expectedColones += t.amount_colones;
        expectedDollars += t.amount_dollars;
      } else {
        // Entrada de banco: depósito al banco, suman porque entraron como venta
        expectedColones += t.amount_colones;
        expectedDollars += t.amount_dollars;
      }
    } else if (t.type === "pago_banco") {
      // Pago desde banco: SUMA al efectivo esperado (el banco cubre el gasto)
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "entrada_banco") {
      // Entrada desde banco/bóveda: dinero ENTRA a caja
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    } else if (t.type === "salida_banco") {
      // Salida hacia banco/bóveda: NO afecta caja ni efectivo esperado
      // Solo rebaja del banco asignado (se maneja en backend)
      return;
    } else if (t.type === "sinpe") {
      // SINPE: El dinero va al banco, NO a caja física
      // SINPE de venta directa: RESTA porque es dinero que no entró a caja
      // SINPE para pago de deuda (debt_operation === "pago"): NO resta porque 
      // la cuenta_por_cobrar ya restó cuando se creó la deuda
      if (t.debt_operation === "pago") {
        // Pago de deuda via SINPE - no afecta porque la deuda ya restó
        return;
      } else {
        // SINPE de venta directa - RESTA porque el dinero fue al banco
        expectedColones -= t.amount_colones;
        expectedDollars -= t.amount_dollars;
      }
    } else if (t.type === "pago_con_premio_afecta") {
      expectedColones += t.amount_colones;
      expectedDollars += t.amount_dollars;
    }
    // no_afecta_caja, pago_dias_anteriores_no_afecta y pago_con_premio NO suman ni restan
  });

  // Add pending values if provided
  if (pendingVentas) {
    const totalPendingVentas = (pendingVentas.venta_macho || 0) + (pendingVentas.venta_tiro || 0);
    const totalPendingPremios = (pendingVentas.premio_macho || 0) + (pendingVentas.premio_tiro || 0);
    expectedColones += totalPendingVentas;
    expectedColones -= totalPendingPremios;
  }

  return { expectedColones, expectedDollars };
}
